import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaEdit,
  FaGlobe,
  FaInfoCircle,
  FaStickyNote,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import { canDeleteInPortal } from "./portalSession";
import SocialPlatformIcon, { SocialPlatformWithLabel } from "../components/SocialPlatformIcon";
import { SOCIAL_PLATFORMS } from "../constants/socialPlatforms";

const ACCOUNT_STATUSES = ["Active", "Inactive"];

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayValue(value)}</span>
    </div>
  );
}

function DetailSection({ icon: Icon, title, children, className = "" }) {
  return (
    <section className={`cportal-card cportal-detail-section ${className}`.trim()}>
      <h2 className="cportal-detail-section-title">
        {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
        {title}
      </h2>
      {children}
    </section>
  );
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

function linkValue(url) {
  if (!url || String(url).trim() === "") return "—";
  const href = String(url).trim();
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="cportal-link">
      {href}
    </a>
  );
}

function toForm(row) {
  return {
    platformName: row?.Platform_Name || "",
    accountName: row?.Account_Name || "",
    username: row?.Username || "",
    password: row?.Password || "",
    profileUrl: row?.Profile_URL || "",
    businessUrl: row?.Business_URL || "",
    emailUsed: row?.Email_Used || "",
    contactNumber: row?.Contact_Number || "",
    status: row?.Status || "Active",
    notes: row?.Notes || "",
  };
}

function buildPayload(form) {
  return {
    Platform_Name: form.platformName.trim(),
    Account_Name: form.accountName.trim(),
    Username: form.username.trim(),
    Password: form.password.trim(),
    Profile_URL: form.profileUrl.trim() || null,
    Business_URL: form.businessUrl.trim() || null,
    Email_Used: form.emailUsed.trim() || null,
    Contact_Number: form.contactNumber.trim() || null,
    Status: form.status || "Active",
    Notes: form.notes.trim() || null,
  };
}

function CustomerSocialAccountView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get(`/client/social-accounts/${id}`);
        if (!active) return;
        const data = res.data?.data || null;
        setAccount(data);
        setForm(toForm(data));
        if (!data) setError("Social account not found.");
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Could not load social account details.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (location.state?.edit) {
      setEditing(true);
    }
  }, [location.state?.edit]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (
      !form.platformName.trim() ||
      !form.accountName.trim() ||
      !form.username.trim() ||
      !form.password.trim()
    ) {
      setMessage({ type: "error", text: "Platform, account name, username, and password are required." });
      return;
    }
    const contactError = mobileValidationMessage(form.contactNumber, { label: "Contact number" });
    if (contactError) {
      setMessage({ type: "error", text: contactError });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await axiosInstance.put(`/client/social-accounts/${id}`, buildPayload(form));
      const data = res.data?.data;
      if (data) {
        setAccount(data);
        setForm(toForm(data));
      }
      setEditing(false);
      setMessage({ type: "success", text: "Social account updated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update social account.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this social account?")) return;

    setDeleting(true);
    setMessage({ type: "", text: "" });
    try {
      await axiosInstance.delete(`/client/social-accounts/${id}`);
      navigate("/portal/social-accounts");
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not deactivate social account.",
      });
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="cportal-page">
        <p className="cportal-muted">Loading…</p>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => navigate("/portal/social-accounts")}
        >
          <FaArrowLeft /> Back to social accounts
        </button>
        <div className="cportal-alert cportal-alert-error">{error || "Social account not found."}</div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => {
            setEditing(false);
            setForm(toForm(account));
            setMessage({ type: "", text: "" });
          }}
        >
          <FaArrowLeft /> Back to account
        </button>

        <form className="cportal-card" style={{ marginTop: 16 }} onSubmit={handleSave} noValidate>
          <div className="cportal-card-head">
            <h2>Edit social account</h2>
          </div>
          {message.text && (
            <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
              {message.text}
            </div>
          )}
          <div className="cportal-form-grid cportal-form-grid--3">
            <div className="cportal-field">
              <label>Platform <span className="cportal-required">*</span></label>
              <select
                value={form.platformName}
                onChange={(e) => setField("platformName", e.target.value)}
                required
              >
                <option value="">Select platform</option>
                {SOCIAL_PLATFORMS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="cportal-field">
              <label>Account name <span className="cportal-required">*</span></label>
              <input
                value={form.accountName}
                onChange={(e) => setField("accountName", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field">
              <label>Username <span className="cportal-required">*</span></label>
              <input value={form.username} onChange={(e) => setField("username", e.target.value)} required />
            </div>
            <div className="cportal-field">
              <label>Password <span className="cportal-required">*</span></label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setField("password", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field">
              <label>Profile URL</label>
              <input
                type="url"
                value={form.profileUrl}
                onChange={(e) => setField("profileUrl", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>Business URL</label>
              <input
                type="url"
                value={form.businessUrl}
                onChange={(e) => setField("businessUrl", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>Email used</label>
              <input
                type="email"
                value={form.emailUsed}
                onChange={(e) => setField("emailUsed", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>Contact number</label>
              <input
                value={form.contactNumber}
                onChange={(e) => setField("contactNumber", sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
              />
            </div>
            <div className="cportal-field">
              <label>
                Status <span className="cportal-required">*</span>
              </label>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)}>
                {ACCOUNT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="cportal-field cportal-field--full">
              <label>Notes</label>
              <textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
            </div>
          </div>
          <div className="cportal-form-actions">
            <button
              type="button"
              className="cportal-btn cportal-btn-ghost"
              onClick={() => {
                setEditing(false);
                setForm(toForm(account));
                setMessage({ type: "", text: "" });
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="cportal-page">
      <button
        type="button"
        className="cportal-back-link"
        onClick={() => navigate("/portal/social-accounts")}
      >
        <FaArrowLeft /> Back to social accounts
      </button>

      {message.text && (
        <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
          {message.text}
        </div>
      )}

      <div className="cportal-entity-hero">
        <div className="cportal-entity-hero-left">
          <div className="cportal-entity-avatar cportal-entity-avatar--platform" aria-hidden="true">
            <SocialPlatformIcon platform={account.Platform_Name} size={28} showBackground />
          </div>
          <div className="cportal-entity-hero-meta">
            <h1 className="cportal-entity-hero-name">{displayValue(account.Account_Name)}</h1>
            <span className={statusBadgeClass(account.Status)}>
              {displayValue(account.Status)}
            </span>
          </div>
        </div>
        <div className="cportal-entity-hero-actions">
          <button
            type="button"
            className="cportal-btn cportal-btn-ghost"
            onClick={() => setEditing(true)}
          >
            <FaEdit aria-hidden="true" /> Edit
          </button>
          {canDelete && (
            <button
              type="button"
              className="cportal-btn cportal-btn-danger-outline"
              onClick={handleDelete}
              disabled={deleting || account.Status === "Inactive"}
            >
              <FaTrash aria-hidden="true" />
              {deleting ? "Deactivating…" : "Delete"}
            </button>
          )}
        </div>
      </div>

      <div className="cportal-detail-grid">
        <DetailSection icon={FaInfoCircle} title="Account information">
          <div className="cportal-profile-view-grid">
            <DetailItem label="Platform">
              <SocialPlatformWithLabel platform={account.Platform_Name} />
            </DetailItem>
            <DetailItem label="Account name" value={account.Account_Name} />
            <DetailItem label="Username" value={account.Username} />
            <DetailItem label="Password" value={account.Password} />
            <DetailItem label="Status" value={account.Status} />
            <DetailItem label="Created on">
              <span className="cportal-datetime-value">
                <FaCalendarAlt aria-hidden="true" />
                {formatDateTime(account.Created_On)}
              </span>
            </DetailItem>
          </div>
        </DetailSection>

        <DetailSection icon={FaUser} title="Contact">
          <div className="cportal-profile-view-grid">
            <DetailItem label="Email used" value={account.Email_Used} />
            <DetailItem label="Contact number" value={account.Contact_Number} />
          </div>
        </DetailSection>
      </div>

      <DetailSection icon={FaGlobe} title="URLs">
        <div className="cportal-profile-view-grid">
          <DetailItem label="Profile URL">{linkValue(account.Profile_URL)}</DetailItem>
          <DetailItem label="Business URL">{linkValue(account.Business_URL)}</DetailItem>
        </div>
      </DetailSection>

      <DetailSection icon={FaStickyNote} title="Notes">
        <div className="cportal-profile-view-grid">
          <DetailItem label="Notes" value={account.Notes} />
        </div>
      </DetailSection>
    </div>
  );
}

export default CustomerSocialAccountView;
